import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Grid, TextField, Button, CircularProgress, Divider, Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr } from 'date-fns/locale';
import { Save } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks';
import { USER_ROLES } from '../../constants/permissions';
import { UPDATE_JOB_PROJECT_DATES, CREATE_JOB_DETAIL } from '../../graphql/mutations/jobs';
import graphqlClient from '../../utils/graphqlClient';
import { formatDateForHasura } from '../../utils/dateUtils';

// Ibrahim (2026-10-26): "Proje durum guncellemede ben ve ekip liderleri
// proje no ve tarihlerini guncelleyebilelim."
//
// Onceden proje no yalnizca TUBITAK isinde ve numara BOSKEN, tarihler de
// yalnizca statu ONAYLANDI'ya cekilirken girilebiliyordu; yanlis girilen
// bir deger statu degistirmeden duzeltilemiyordu.
//
// NASIL YAZILIYOR:
//   - Proje no jobs.project_number kolonunda → dogrudan guncellenir.
//   - Tarihler job_details.detail_data icinde. Eski kaydi EZMIYORUZ, statu
//     degismeden yeni bir gecmis satiri ekliyoruz. Tarihleri okuyan her yer
//     (ciro hesabi pickJobProjectDates, ciro uyarilari, modal basligi)
//     job_details'i en yeniden eskiye tarayip ilk dolu degeri aldigi icin
//     duzeltme her yerde gecerli olur; eski deger de Gecmis'te kalir.
//   - Degismeyen tarihler de yeni satira yaziliyor: okuyucular baslangic ve
//     bitisi ayni satirdan bekleyen yerler var (revenueWarnings).
export const PROJECT_INFO_EDIT_ROLES = new Set([USER_ROLES.ROOT, USER_ROLES.TEAM_MANAGER]);

const latestDetailValue = (job, key) => {
  for (const d of job?.job_details || []) {
    const v = d?.detail_data?.[key];
    if (v) return v;
  }
  return '';
};

const ProjectInfoEditSection = ({ job, isProjectBased, jobTypeEnum, onSuccess, onError, onRefresh, styles }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const initial = useMemo(() => ({
    projectNumber: job?.project_number != null ? String(job.project_number) : '',
    approvalDate: latestDetailValue(job, 'approvalDate'),
    projectStartDate: latestDetailValue(job, 'projectStartDate'),
    projectEndDate: latestDetailValue(job, 'projectEndDate'),
    notes: '',
  }), [job]);
  const [form, setForm] = useState(initial);
  useEffect(() => { setForm(initial); setError(null); }, [initial]);

  if (!PROJECT_INFO_EDIT_ROLES.has(user?.role)) return null;

  // Onay tarihi yalnizca daha once girilmisse gosteriliyor; hic onaylanmamis
  // bir ise buradan onay tarihi yazmak statu akisini atlamak olur.
  const showApprovalDate = Boolean(initial.approvalDate);
  const dateKeys = [
    showApprovalDate && 'approvalDate',
    isProjectBased && 'projectStartDate',
    isProjectBased && 'projectEndDate',
  ].filter(Boolean);

  const numberChanged = form.projectNumber.trim() !== initial.projectNumber;
  const datesChanged = dateKeys.some((k) => (form[k] || '') !== (initial[k] || ''));

  const handleSave = async () => {
    const raw = form.projectNumber.trim();
    const numberValue = raw === '' ? null : Number(raw);
    if (raw !== '' && !Number.isInteger(numberValue)) {
      setError(t('jobStatusUpdate.projectInfo.invalidNumber'));
      return;
    }
    if (isProjectBased && form.projectStartDate && form.projectEndDate
        && form.projectEndDate < form.projectStartDate) {
      setError(t('jobStatusUpdate.projectInfo.endBeforeStart'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (numberChanged) {
        await graphqlClient.mutate(UPDATE_JOB_PROJECT_DATES, {
          id: job.id,
          set: { project_number: numberValue },
        });
      }
      const detailData = {
        notes: form.notes.trim() || null,
        oldStatus: job.job_status,
        newStatus: job.job_status,
        statusChange: t('jobStatusUpdate.projectInfo.historyLabel'),
        projectInfoCorrection: true,
        projectNumber: numberChanged ? (numberValue == null ? null : String(numberValue)) : null,
      };
      if (datesChanged) {
        dateKeys.forEach((k) => { detailData[k] = form[k] || null; });
      }
      await graphqlClient.mutate(CREATE_JOB_DETAIL, {
        job_id: job.id,
        // job_detail_type, job_type enum tablosuna FK. Isin son gecmis
        // satirindaki tip o FK'dan zaten gecmis bir deger; once onu kullan.
        job_detail_type: job?.job_details?.[0]?.job_detail_type || jobTypeEnum,
        detail_data: detailData,
        created_by: user?.id,
      });
      onSuccess?.(t('jobStatusUpdate.projectInfo.success'));
      onRefresh?.();
    } catch (err) {
      const msg = err.message || t('jobStatusUpdate.errorMessage');
      setError(msg);
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Divider sx={styles?.divider} />
      <Box sx={styles?.section}>
        <Typography variant="h6" color="text.primary" sx={styles?.sectionTitle}>
          {t('jobStatusUpdate.projectInfo.title')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('jobStatusUpdate.fields.projectNumber')}
              value={form.projectNumber}
              onChange={(e) => setForm((p) => ({ ...p, projectNumber: e.target.value.replace(/\D/g, '') }))}
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
            />
          </Grid>
          {dateKeys.map((k) => (
            <Grid item xs={12} sm={6} key={k}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
                <DatePicker
                  label={t(`jobStatusUpdate.fields.${k}`)}
                  format="dd.MM.yyyy"
                  views={['year', 'month', 'day']}
                  slotProps={{ textField: { fullWidth: true } }}
                  value={form[k] ? new Date(form[k]) : null}
                  onChange={(v) => setForm((p) => ({ ...p, [k]: formatDateForHasura(v) || '' }))}
                />
              </LocalizationProvider>
            </Grid>
          ))}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('jobStatusUpdate.projectInfo.note')}
              value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            />
          </Grid>
        </Grid>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || (!numberChanged && !datesChanged)}
            startIcon={loading ? <CircularProgress size={20} /> : <Save />}
          >
            {t('jobStatusUpdate.projectInfo.save')}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default ProjectInfoEditSection;
